// Round-trip URL search params <-> typed filter object for the VAT
// management page. Non-throwing per-field coercers so a malformed URL
// (e.g. `?page=banana` or `?page=0`) silently falls back to defaults
// instead of handing the user TanStack Router's unbranded error screen.
// Mirrors the `validateProductsSearch` convention.

export type VatSortField = 'title' | 'percent';
export type VatSortOrder = 'asc' | 'desc';

export type VatSearch = {
  page?: number;
  limit?: number;
  sort?: VatSortField;
  order?: VatSortOrder;
};

const asPositiveInt = (v: unknown): number | undefined => {
  const n = typeof v === 'string' ? Number(v) : v;
  return typeof n === 'number' && Number.isInteger(n) && n >= 1 ? n : undefined;
};

const asLimit = (v: unknown): number | undefined => {
  const n = asPositiveInt(v);
  return n !== undefined && n <= 200 ? n : undefined;
};

const asSort = (v: unknown): VatSortField | undefined =>
  v === 'title' || v === 'percent' ? v : undefined;

const asOrder = (v: unknown): VatSortOrder | undefined =>
  v === 'asc' || v === 'desc' ? v : undefined;

export const validateVatSearch = (
  input: Record<string, unknown>
): VatSearch => ({
  page: asPositiveInt(input.page),
  limit: asLimit(input.limit),
  sort: asSort(input.sort),
  order: asOrder(input.order),
});

// Generic siblings-preserving merger so a future filter doesn't clobber
// pagination, mirroring the products page's helper.
export const mergeSearch = (
  prev: Record<string, unknown>,
  patch: Partial<VatSearch>
): Record<string, unknown> => ({ ...prev, ...patch });
